import { FC } from "react";

import Navbar from "@Components/Navbar/Navbar";
import wrapperHOC from "@Utils/wrapperHOC";
import { LEADS_LIST } from "constants/configConstants";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import LoginDispatcher from "@Components/Login/actions";
import { logoutRequest } from "@Components/Login/components/helper";
import useCustomWindowLocation from "@Components/CustomWindowLocation";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { EVENTS } from "@Pages/List/Desktop/constants";

/**
 * Where "Email Us" goes.
 *
 * ⚠️  THE GENERAL ADDRESS, not the hiring one. Career and /job-opening point their
 * own Contact CTA at `hiring@` — this page is for prospective listing partners, so it
 * takes the address the rest of the site publishes.
 */
const CONTACT_EMAIL = "contact@amberstudent.com";

/** Where a partner with dashboard access is sent. */
const CONNECT_URL = "/connect";

/**
 * List With Us's header.
 *
 * ── This used to be a second navbar ────────────────────────────────────────
 * It hand-rolled the whole bar — its own <nav>, its own logo link, its own
 * scroll listener — while importing the shared navbar's STYLESHEET and composing
 * class names out of it. Two pages did that, plus a fork of the component in
 * About Us, so one bar had four implementations.
 *
 * Everything it did is now a prop on `@Components/Navbar`:
 *
 *   brand="b2b"                    the solid blue CTA (partner-facing) rather
 *                                  than the pink gradient (student-facing)
 *   compactLinks                   14px/500 links instead of 16px/600
 *   ctaRevealAfter="[data-lwu-hero]"  keeps the bar's CTA collapsed until the
 *                                  hero's midpoint passes the top, so the bar
 *                                  does not repeat a CTA the hero is showing
 *
 * The mobile bar (wordmark + one CTA + menu) comes with the component.
 *
 * ── THE ANNOUNCEMENT RAIL IS STILL GONE ───────────────────────────────────
 * A 42px blue strip used to sit above the bar, and the two travelled as one
 * fixed stack that slid up by the rail's measured height on first scroll. The
 * rail and the slide went together; this file is what is left, and it is now
 * thin enough to be a configuration rather than a component.
 */
interface HeaderProps {
  /** Opens the listing form. Supplied by the page, which owns the modal. */
  onListClick: () => void;
}

/**
 * The bar's right-hand side is auth-aware, exactly as the page this replaces was
 * (see List/Desktop/components/PartnerHeader). Three states:
 *
 *   logged out                        → "Login", opens the partner login modal
 *   logged in WITH partner access     → "Dashboard", goes to amber connect
 *   logged in WITHOUT partner access  → "Logout"
 *
 * `/login` is NOT a route in this app — the old header dispatched a modal, and so
 * does this one. Shipping the link the design carried would have been a dead end.
 */
// No `useTranslationHook` here on purpose: everything this component names is a
// translation KEY passed to `Navbar`, which resolves it. Resolving here would put
// display text through a prop that is documented as taking a key.
const Header: FC<HeaderProps> = ({ onListClick }) => {
  const dispatch = useDispatch();
  const customWindowLocation = useCustomWindowLocation();
  const { isUserLoggedIn, hasPartnerDashboardAccess } = useSelector(
    ({ appData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      hasPartnerDashboardAccess: appData.hasPartnerDashboardAccess,
    }),
    shallowEqual,
  );

  const handleLoginClick = () => {
    gtagEventEmitter({ action: EVENTS.loginButtonClick, category: EVENTS.category });
    if (isUserLoggedIn && hasPartnerDashboardAccess) {
      customWindowLocation({ url: "/connect" });
      return;
    }
    new LoginDispatcher(dispatch).showLoginModal({ openingSource: "partner_header" });
  };

  const handleLogoutClick = async () => {
    const { data, error } = await logoutRequest();
    if (data) {
      // Same as the old header: drop the partner cookie and reload, so every
      // component re-reads auth from scratch rather than guessing.
      document.cookie = "preferred_partner=;Path=/;";
      window.location.reload();
    } else {
      console.error(error);
    }
  };

  /**
   * A partner who is already signed in should not be sold a signup form.
   *
   * `isPartner` swaps the PRIMARY CTA from "List on amber" — which opens the lead form
   * for prospective partners — to "Go to Dashboard". Offering someone who already has a
   * dashboard the form to apply for one is the wrong ask, and it is the loudest control
   * on the page.
   *
   * Dashboard therefore moves OUT of the nav links and into the CTA, so the two do not
   * say the same thing twice; the nav keeps Logout, which is still useful there.
   *
   * ⚠️  MOSTLY A SAFETY NET, not the common path. `appReducer` redirects a signed-in
   * partner to /connect during SSR (`req.path === "/list"`), so they usually never see
   * this header. It matters where that check does not reach: client-side navigation into
   * /list, and the localised routes — the route is ["/list", "/:lang(es|de|fr)/list"] but
   * the redirect tests the path exactly, so /es/list falls through to the client.
   */
  const isPartner = isUserLoggedIn && hasPartnerDashboardAccess;

  let accountLabel = "navbar.v2.login";
  let accountAction = handleLoginClick;
  if (isUserLoggedIn) {
    accountLabel = "list.v2.header.logout";
    accountAction = handleLogoutClick;
  }

  return (
    <Navbar
      brand="b2b"
      compactLinks
      links={[
        {
          // ⚠️  THIS REPLACES THE WHOLE SUPPORT MENU. It was prod's contact panel —
          // the Zendesk widget, WhatsApp, Messenger, a per-country phone number and
          // email, behind a chevron, desktop-only because the two-column layout needs
          // ~600px. A listing partner wants one address, so the panel, its chevron
          // animation, the `md` gate it needed and the `clicked_support` event are all
          // gone with it.
          //
          // A `mailto:` rather than /contact: that page is the student support desk,
          // and it would put a form between a partner and an inbox.
          label: "home.contactus.emailus",
          href: `mailto:${CONTACT_EMAIL}`,
        },
        { label: accountLabel, href: CONNECT_URL, onClick: accountAction },
      ]}
      ctaLabel={isPartner ? "list.v2.cta.dashboard" : "list.v2.cta.liston"}
      ctaHref={isPartner ? CONNECT_URL : LEADS_LIST}
      // A partner's CTA is a real navigation, so it keeps the href and takes no
      // handler — only the prospective partner's opens the form.
      ctaOnClick={isPartner ? undefined : onListClick}
      ctaRevealAfter="[data-lwu-hero]"
    />
  );
};

export default wrapperHOC(Header, {
  componentName: "Header-ListWithUs",
  showForChina: true,
});
